// selectFirstAvailableOption
// checkAndSelectFirstAvailable
// this.checkAndSelectFirstAvailable()
// added these functions to handle first available options


import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    //abdullah added this 
    this.checkAndSelectFirstAvailable();

     // Initialize custom notes on page load
    const currentSelection = this.selectedOption;
    if (currentSelection) {
      this.handleCustomNotes(currentSelection);
    }

  }

  /**
   * Selects the first available option in a fieldset
   * @param {number} fieldsetIndex - The index of the fieldset
   */
  // selectFirstAvailableOption(fieldsetIndex) {
  //   const radios = this.#radios[fieldsetIndex];
  //   if (!radios) return;

  //   // Find the first available (not disabled and not hidden) radio button
  //   const firstAvailable = radios.find((radio) => {
  //     const label = radio.closest('.variant-option__button-label');
  //     const isHidden = label && window.getComputedStyle(label).display === 'none';
  //     const isAvailable = radio.dataset.optionAvailable === 'true';
  //     return isAvailable && !isHidden;
  //   });

  //   if (firstAvailable && !firstAvailable.checked) {
  //     // Programmatically trigger the change
  //     firstAvailable.checked = true;
  //     const changeEvent = new Event('change', { bubbles: true });
  //     firstAvailable.dispatchEvent(changeEvent);
  //   }
  // }


  selectFirstAvailableOption(fieldsetIndex) {
  const radios = this.#radios[fieldsetIndex];
  if (!radios) return;

  // Get the current type selection
  const typeFieldset = this.refs.fieldsets?.find(fs => fs.dataset.fieldsetIndex === '0');
  const selectedType = typeFieldset?.querySelector('input:checked')?.value?.trim().toLowerCase();

  // Find the first available (not disabled and not hidden) radio button
  const firstAvailable = radios.find((radio) => {
    const label = radio.closest('.variant-option__button-label');
    const isHidden = label && window.getComputedStyle(label).display === 'none';
    const isAvailable = radio.dataset.optionAvailable === 'true';
    
    // For size fieldset (index 1), exclude freesize when type is stitched
    if (fieldsetIndex === 1 && selectedType === 'stitched') {
      const isFreesize = radio.value?.trim().toLowerCase() === 'freesize' || 
                        radio.value?.trim().toLowerCase() === 'free size' ||
                        radio.dataset.optionValue?.trim().toLowerCase() === 'freesize' ||
                        radio.dataset.optionValue?.trim().toLowerCase() === 'free size';
      
      return isAvailable && !isHidden && !isFreesize;
    }
    
    return isAvailable && !isHidden;
  });

  if (firstAvailable && !firstAvailable.checked) {
    // Programmatically trigger the change
    firstAvailable.checked = true;
    const changeEvent = new Event('change', { bubbles: true });
    firstAvailable.dispatchEvent(changeEvent);
  }
}

/**
   * Shows/hides custom notes based on variant type selection
   * @param {HTMLElement} selectedOption
   */
  handleCustomNotes(selectedOption) {
    const selectedValue = selectedOption.value?.toLowerCase(); // Gets "unstitched" or "stitched"
    
    const unstitchedNote = document.querySelector('.custom-unstitched-note');
    const stitchedNote = document.querySelector('.custom-stitched-note');
    
    if (!unstitchedNote || !stitchedNote) return; // Exit if elements don't exist
    
    // Hide both first
    unstitchedNote.style.display = 'none';
    stitchedNote.style.display = 'none';
    
    // Show the relevant one
    if (selectedValue === 'unstitched') {
      unstitchedNote.style.display = 'block';
      console.log(unstitchedNote);
    } else if (selectedValue === 'stitched') {
      stitchedNote.style.display = 'block';
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    // Handle custom notes visibility
    this.handleCustomNotes(selectedOption);

    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // After updating the variant picker, check for hidden/disabled options and select first available
          this.checkAndSelectFirstAvailable();

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Checks all fieldsets and selects the first available option if current is hidden/disabled
   */
  // checkAndSelectFirstAvailable() {
  //   const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    
  //   fieldsets.forEach((fieldset, fieldsetIndex) => {
  //     const radios = this.#radios[fieldsetIndex];
  //     if (!radios) return;

  //     // Find the currently checked radio
  //     const currentChecked = radios.find((radio) => radio.checked);
      
  //     if (currentChecked) {
  //       const label = currentChecked.closest('.variant-option__button-label');
  //       const isHidden = label && window.getComputedStyle(label).display === 'none';
  //       const isUnavailable = currentChecked.dataset.optionAvailable === 'false';

  //       // If current selection is hidden or unavailable, select first available
  //       if (isHidden || isUnavailable) {
  //         this.selectFirstAvailableOption(fieldsetIndex);
  //       }
  //     } else {
  //       // If nothing is checked, select first available
  //       this.selectFirstAvailableOption(fieldsetIndex);
  //     }
  //   });
  // }

// checkAndSelectFirstAvailable() {
//   const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

//   // Defer the logic to the next animation frame so styles are up to date
//   requestAnimationFrame(() => {
//     /** -----------------------------------
//      * Hide or show size variant based on type FIRST
//      * ----------------------------------- */
//     const typeFieldset = fieldsets.find(fs => fs.dataset.fieldsetIndex === '0');
//     const sizeFieldset = fieldsets.find(fs => fs.dataset.fieldsetIndex === '1');

//     if (!typeFieldset || !sizeFieldset) return;

//     const selectedType = typeFieldset.querySelector('input:checked')?.value?.trim().toLowerCase();

//     if (selectedType === 'unstitched') {
//       // Hide size fieldset completely
//       sizeFieldset.style.display = 'none';

//       // Clear any selected size and reset state completely
//       const sizeRadios = this.#radios[1] || [];
//       sizeRadios.forEach(radio => { 
//         radio.checked = false;
//         radio.dataset.currentChecked = 'false';
//         radio.dataset.previousChecked = 'false';
//       });
      
//       // Clear the checked indices for size fieldset
//       if (this.#checkedIndices[1]) {
//         this.#checkedIndices[1] = [];
//       }

//       // Reset CSS custom properties
//       sizeFieldset.style.removeProperty('--pill-width-current');
//       sizeFieldset.style.removeProperty('--pill-width-previous');

//     } else {
//       // Show size fieldset
//       sizeFieldset.style.display = '';
      
//       // Reset CSS custom properties first
//       sizeFieldset.style.removeProperty('--pill-width-current');
//       sizeFieldset.style.removeProperty('--pill-width-previous');

//       // Force auto-select first available size when switching to stitched
//       // Use multiple timeouts to ensure proper state management
//       setTimeout(() => {
//         const anyChecked = sizeFieldset.querySelector('input:checked');
//         const anyCurrentlyActive = sizeFieldset.querySelector('input[data-current-checked="true"]');
        
//         // If nothing is checked OR nothing is marked as currently active, select first available
//         if (!anyChecked || !anyCurrentlyActive) {
//           // Clear any existing selections first
//           const sizeRadios = this.#radios[1] || [];
//           sizeRadios.forEach(radio => { 
//             radio.checked = false;
//             radio.dataset.currentChecked = 'false';
//             radio.dataset.previousChecked = 'false';
//           });
          
//           // Reset the checked indices
//           if (this.#checkedIndices[1]) {
//             this.#checkedIndices[1] = [];
//           }
          
//           // Now select the first available
//           this.selectFirstAvailableOption(1);
//         }
//       }, 200); // Increased delay for better reliability during toggling
//     }

//     // Handle other fieldsets AFTER type/size logic
//     fieldsets.forEach((fieldset, fieldsetIndex) => {
//       // Skip the size fieldset if it's hidden
//       if (fieldsetIndex === 1 && selectedType === 'unstitched') {
//         return;
//       }

//       const radios = this.#radios[fieldsetIndex];
//       if (!radios) return;

//       const currentChecked = radios.find((radio) => radio.checked);
//       if (currentChecked) {
//         const label = currentChecked.closest('.variant-option__button-label');
//         const isHidden = label && window.getComputedStyle(label).display === 'none';
//         const isUnavailable = currentChecked.dataset.optionAvailable === 'false';

//         if (isHidden || isUnavailable) {
//           this.selectFirstAvailableOption(fieldsetIndex);
//         }
//       } else if (fieldsetIndex !== 1) {
//         // Only auto-select for non-size fieldsets here
//         this.selectFirstAvailableOption(fieldsetIndex);
//       }
//     });
//   });
// }

checkAndSelectFirstAvailable() {
  const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

  // Defer the logic to the next animation frame so styles are up to date
  requestAnimationFrame(() => {
    /** -----------------------------------
     * Handle size selection based on type
     * ----------------------------------- */
    const typeFieldset = fieldsets.find(fs => fs.dataset.fieldsetIndex === '0');
    const sizeFieldset = fieldsets.find(fs => fs.dataset.fieldsetIndex === '1');

    if (!typeFieldset || !sizeFieldset) return;

    const selectedType = typeFieldset.querySelector('input:checked')?.value?.trim().toLowerCase();

    if (selectedType === 'unstitched') {
      // Hide size fieldset but keep freesize selected in background
      sizeFieldset.style.display = 'none';
      
      // Reset CSS custom properties
      sizeFieldset.style.removeProperty('--pill-width-current');
      sizeFieldset.style.removeProperty('--pill-width-previous');

      // Find and select freesize option in the background (only if not already selected)
      setTimeout(() => {
        const sizeRadios = this.#radios[1] || [];
        const freesizeOption = sizeRadios.find(radio => 
          radio.value?.trim().toLowerCase() === 'freesize' || 
          radio.value?.trim().toLowerCase() === 'free size' ||
          radio.dataset.optionValue?.trim().toLowerCase() === 'freesize' ||
          radio.dataset.optionValue?.trim().toLowerCase() === 'free size'
        );

        // ✅ CRITICAL FIX: Only select if freesize is not already selected
        if (freesizeOption && !freesizeOption.checked) {
          // Clear any existing selections first
          sizeRadios.forEach(radio => { 
            radio.checked = false;
            radio.dataset.currentChecked = 'false';
            radio.dataset.previousChecked = 'false';
          });
          
          // Reset the checked indices
          if (this.#checkedIndices[1]) {
            this.#checkedIndices[1] = [];
          }
          
          // Select freesize (but fieldset remains hidden)
          freesizeOption.checked = true;
          freesizeOption.dataset.currentChecked = 'true';
          
          // Update the checked indices for freesize
          const freesizeIndex = sizeRadios.indexOf(freesizeOption);
          if (freesizeIndex !== -1) {
            if (!this.#checkedIndices[1]) {
              this.#checkedIndices[1] = [];
            }
            this.#checkedIndices[1].unshift(freesizeIndex);
            this.#checkedIndices[1].length = Math.min(this.#checkedIndices[1].length, 2);
          }
          
          // ✅ CRITICAL FIX: Trigger change event ONLY when we actually change the selection
          const changeEvent = new Event('change', { bubbles: true });
          freesizeOption.dispatchEvent(changeEvent);
        }

          // Update custom notes after auto-selection
          const currentSelection = this.selectedOption;
          if (currentSelection) {
            this.handleCustomNotes(currentSelection);
          }
      }, 150);

    } else {
      // Show size fieldset and select first available size for stitched
      sizeFieldset.style.display = '';
      
      // Reset CSS custom properties
      sizeFieldset.style.removeProperty('--pill-width-current');
      sizeFieldset.style.removeProperty('--pill-width-previous');

      // Auto-select first available size when switching to stitched
      setTimeout(() => {
        const anyChecked = sizeFieldset.querySelector('input:checked');
        const anyCurrentlyActive = sizeFieldset.querySelector('input[data-current-checked="true"]');
        
        // Check if current selection is freesize (which shouldn't be visible for stitched)
        const currentChecked = sizeFieldset.querySelector('input:checked');
        const isFreesizeSelected = currentChecked && (
          currentChecked.value?.trim().toLowerCase() === 'freesize' || 
          currentChecked.value?.trim().toLowerCase() === 'free size' ||
          currentChecked.dataset.optionValue?.trim().toLowerCase() === 'freesize' ||
          currentChecked.dataset.optionValue?.trim().toLowerCase() === 'free size'
        );
        
        // If nothing is checked, nothing is active, or freesize is selected, select first available
        if (!anyChecked || !anyCurrentlyActive || isFreesizeSelected) {
          // Clear any existing selections first
          const sizeRadios = this.#radios[1] || [];
          sizeRadios.forEach(radio => { 
            radio.checked = false;
            radio.dataset.currentChecked = 'false';
            radio.dataset.previousChecked = 'false';
          });
          
          // Reset the checked indices
          if (this.#checkedIndices[1]) {
            this.#checkedIndices[1] = [];
          }
          
          // Now select the first available (non-freesize) option
          this.selectFirstAvailableOption(1);
        }

          // Update custom notes after auto-selection
          const currentSelection = this.selectedOption;
          if (currentSelection) {
            this.handleCustomNotes(currentSelection);
          }
      }, 150);

    }

    // Handle other fieldsets AFTER type/size logic
    fieldsets.forEach((fieldset, fieldsetIndex) => {
      // Skip the size fieldset as we've already handled it above
      if (fieldsetIndex === 1) {
        return;
      }

      const radios = this.#radios[fieldsetIndex];
      if (!radios) return;

      const currentChecked = radios.find((radio) => radio.checked);
      if (currentChecked) {
        const label = currentChecked.closest('.variant-option__button-label');
        const isHidden = label && window.getComputedStyle(label).display === 'none';
        const isUnavailable = currentChecked.dataset.optionAvailable === 'false';

        if (isHidden || isUnavailable) {
          this.selectFirstAvailableOption(fieldsetIndex);
        }
      } else {
        // Only auto-select for non-size fieldsets here
        this.selectFirstAvailableOption(fieldsetIndex);
      }
    });
  });
}
  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}